const areas = require("./areas.json");

/**
 * Areas that get their own landing page at /estate-agents/<slug>.
 *
 * NAMING: this file is deliberately NOT called locations.js. `Locations.js`
 * already exists in this directory (the search-filter dropdown list), and on
 * a case-insensitive filesystem - which is to say, on Windows - the two names
 * are the same file. Writing one silently destroys the other, and the failure
 * only surfaces at build time as "'Locations' is not exported".
 *
 * The data itself lives in areas.json because two very different consumers
 * need it: this module (bundled by webpack) and
 * api/scripts/generate-sitemap.js (plain Node on the server, which cannot
 * import an ES module). One JSON file keeps the slug list from drifting
 * between the site and the sitemap.
 *
 * WHY THESE AREAS AND NOT ALL OF THEM: the database holds 58 distinct
 * `location` values, but most have one to three properties. A page built on
 * three listings is thin content, and thin pages drag a site down rather than
 * lifting it. Only areas with roughly six or more published properties are
 * included - 14 areas covering 231 of the 281 published listings.
 *
 * `locations` is a list because one area is stored under several names:
 * Tooting and Tooting Broadway are one place to a searcher, and Clapham is
 * split five ways. These are matched exactly (`$in` in the api), so they must
 * stay spelled exactly as they appear in the database.
 *
 * `intro` is deliberately empty. These pages deserve a paragraph of real
 * local knowledge in Property People's own voice - which station, which
 * streets, what sort of stock comes up. That is the agency's to write, not
 * something to invent here. The page reads fine without it and simply omits
 * the paragraph while it is blank.
 */
const AREAS = areas;

function findArea(slug) {
    if (!slug) return null;
    return AREAS.find((a) => a.slug === String(slug).toLowerCase()) || null;
}

/** Facts counted from the records themselves - nothing here is asserted. */
function areaStats(properties) {
    const list = properties || [];
    const live = list.filter((p) => p.status === "Available" || p.status === "Under Offer");
    const lettings = list.filter((p) => p.category !== "Sales");
    const sales = list.filter((p) => p.category === "Sales");

    const types = [];
    list.forEach((p) => {
        if (p.type && types.indexOf(p.type) === -1) types.push(p.type);
    });

    const bedCounts = list.map((p) => p.bedrooms).filter((b) => typeof b === "number");
    const rentPrices = lettings.map((p) => p.price).filter((n) => typeof n === "number" && n > 0);
    const salePrices = sales.map((p) => p.price).filter((n) => typeof n === "number" && n > 0);

    return {
        total: list.length,
        live: live.length,
        lettings: lettings.length,
        sales: sales.length,
        types: types.sort(),
        minBeds: bedCounts.length ? Math.min.apply(null, bedCounts) : null,
        maxBeds: bedCounts.length ? Math.max.apply(null, bedCounts) : null,
        minRent: rentPrices.length ? Math.min.apply(null, rentPrices) : null,
        maxRent: rentPrices.length ? Math.max.apply(null, rentPrices) : null,
        minSale: salePrices.length ? Math.min.apply(null, salePrices) : null,
        maxSale: salePrices.length ? Math.max.apply(null, salePrices) : null,
        lettingsLed: lettings.length >= sales.length,
    };
}

/*
 * CommonJS on purpose: api/render.js requires this directly so the HTML the
 * server sends and the tags React sets in the browser come from one
 * implementation. Two copies would drift, and a page whose served title
 * differs from its rendered title sends a search engine mixed signals.
 * webpack resolves the named imports elsewhere in the app unchanged.
 */
module.exports = {
    AREAS,
    findArea,
    areaStats,
};
